import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Signin.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { showToast } from "../../utils/customToast";

function Signin() {
  const [username, setUsername] = useState(() => {
    const isRemembered = localStorage.getItem("rememberMe") === "true";
    return isRemembered ? localStorage.getItem("savedUsername") || "" : "";
  });
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem("rememberMe") === "true";
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const usernameRef = useRef(username);
  const passwordRef = useRef("");

  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

  // Keep usernameRef synced
  useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  // If already logged in, redirect away from login screen
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const userType = localStorage.getItem("userType");
    const selectedComp = localStorage.getItem("selectedCompany");

    if (userId) {
      if (userType === "9" || userType === "1" || selectedComp) {
        navigate("/home");
      } else if (userType === "0") {
        navigate("/select-company");
      }
    }
  }, [navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;

    const u = (usernameRef.current || username).trim();
    const p = passwordRef.current || password;

    if (!u || !p) {
      showToast.warn("Please enter both Mobile No / Email and password.");
      return;
    }

    setLoading(true);

    try {
      const apiUrl = `${BASE_API_URL}/auth/login`;

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Username: u,
          Password: p,
        }),
      });

      const data = await res.json();
      console.log("Login Response:", data);

      if (data && data.success) {
        const role = data.role;
        const user = data.user;

        // Remember me handling
        if (rememberMe) {
          localStorage.setItem("rememberMe", "true");
          localStorage.setItem("savedUsername", u);
        } else {
          localStorage.removeItem("rememberMe");
          localStorage.removeItem("savedUsername");
        }

        // ===== Common Data =====
        localStorage.setItem("username", user.Name || u);
        localStorage.setItem("userId", String(user.userId));
        localStorage.setItem("role", role);
        if (user.MaxCompanies !== undefined && user.MaxCompanies !== null) {
          localStorage.setItem("maxCompanies", String(user.MaxCompanies));
        }
        if (data.token) {
          localStorage.setItem("token", data.token);
        }

        showToast.success("Sign-in successful!");

        setTimeout(() => {
          // ==========================
          // SUPER ADMIN (UserType 9)
          // ==========================
          if (role === "SuperAdmin") {
            localStorage.setItem("userType", "9");
            localStorage.removeItem("companyId");
            localStorage.removeItem("companyName");
            localStorage.removeItem("selectedCompany");
            localStorage.removeItem("companyList");

            window.dispatchEvent(new Event("companyChanged"));
            navigate("/home");
            return;
          }

          // ==========================
          // ADMIN (UserType 0)
          // ==========================
          if (role === "Admin") {
            localStorage.setItem("userType", "0");
            localStorage.removeItem("selectedCompany");
            localStorage.removeItem("companyId");
            localStorage.removeItem("companyName");

            const companies = user.companies || [];
            if (Array.isArray(companies) && companies.length > 0) {
              localStorage.setItem("companyList", JSON.stringify(companies));

              // If only 1 company exists, auto-select it directly
              if (companies.length === 1) {
                const comp = companies[0];
                const compId = comp.CompID || comp.CompId;
                const compName = comp.Name;

                localStorage.setItem("companyId", compId);
                localStorage.setItem("companyName", compName);
                localStorage.setItem(
                  "selectedCompany",
                  JSON.stringify({
                    id: compId,
                    name: compName,
                    LogoPath: comp.LogoPath || null,
                    CompanyCode: comp.CompanyCode || "",
                    CompanyColourCode: comp.CompanyColourCode || "",
                  })
                );

                window.dispatchEvent(new Event("companyChanged"));
                navigate("/home");
                return;
              }
            }

            // Multiple or 0 companies -> go to selection page
            navigate("/select-company");
            window.dispatchEvent(new Event("companyChanged"));
            return;
          }

          // ==========================
          // NORMAL USER (UserType 1)
          // ==========================
          if (role === "User") {
            localStorage.setItem("userType", "1");
            localStorage.removeItem("companyList");

            if (user.CompId) {
              localStorage.setItem("companyId", String(user.CompId));
              localStorage.setItem("companyName", user.CompanyName || "");

              localStorage.setItem(
                "selectedCompany",
                JSON.stringify({
                  id: user.CompId,
                  name: user.CompanyName || "",
                })
              );
            } else {
              localStorage.removeItem("companyId");
              localStorage.removeItem("companyName");
              localStorage.removeItem("selectedCompany");
            }

            window.dispatchEvent(new Event("companyChanged"));
            navigate("/home");
            return;
          }
        }, 300);
      } else {
        showToast.error(data?.message || "Invalid username or password.");
      }
    } catch (error) {
      console.error("Login error:", error);
      showToast.error("Login failed. Please check server connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sign_in-container">
      <div className="signin_div">
        <div className="signin_logo">
          <img src="./media/response_info.jpg" alt="Response Infoway" />
        </div>

        <form className="login_page" onSubmit={handleLogin}>
          <h2 className="login_title">Welcome Back!</h2>
          <p className="login_subtitle">Login to continue to etour</p>

          <div className="form-group mb-3">
            <label className="form-label" htmlFor="login-username">Username</label>
            <input
              id="login-username"
              type="text"
              className="form-control"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                usernameRef.current = e.target.value;
              }}
              placeholder="Enter Mobile No or Email"
              autoComplete="username"
              required
            />
          </div>

          <div className="form-group mb-3">
            <label className="form-label" htmlFor="login-password">Password</label>
            <div className="password-wrapper">
              <input
                id="login-password"
                className="form-control password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  passwordRef.current = e.target.value;
                }}
                placeholder="Enter Password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="password-eye-btn"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="rember_row">
            <label className="remember-label" htmlFor="rememberMe">
              <input
                className="form-check-input remember-checkbox"
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="forgot-password-link">
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            className="btn btn-primary-theme"
            id="login_btn"
            disabled={loading}
          >
            {loading ? (
              <span className="btn-loading-wrapper">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Logging in...</span>
              </span>
            ) : (
              "Log in"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Signin;
